import logger from '../../utils/logger';
import React, { useState } from "react";
import { toast } from "react-toastify";
import "../../style/NewImagingOrder.css";
import "../../style/ImagingModals.css";

const labTests = [
  { code: "85025", name: "Blood count; complete (CBC) with automated differential", type: "Blood" },
  { code: "82670", name: "Estradiol (E2)", type: "Blood" },
  { code: "83001", name: "Gonadotropin, follicle stimulating (FSH)", type: "Blood" },
  { code: "83002", name: "Gonadotropin, luteinizing (LH)", type: "Blood" },
  { code: "84144", name: "Progesterone (reproductive hormone) level", type: "Blood" },
  { code: "84439", name: "Thyroxine (T4), free", type: "Blood" },
  { code: "84481", name: "Triiodothyronine (T3), free", type: "Blood" },
  { code: "84443", name: "Thyroid stimulating hormone (TSH)", type: "Blood" },
  { code: "84702", name: "Quantitative Beta HCG", type: "Blood" },
  { code: "82962", name: "Glucose (Blood)", type: "Blood" },
];

const LAB_PROFILES = [
  { code: "80053", name: "Comprehensive Metabolic Panel" },
  { code: "80076", name: "Hepatic Function Panel" },
  { code: "84443", name: "TSH with reflex T4, Free T3 Total if Abnormal" },
];

const initialState = {
  firstName: "",
  lastName: "",
  dob: "",
  sex: "",
  externalMrn: "",
  orderingClinician: "",
  diagnosis: "",
  specimenDate: "",
  specimenTime: "",
  drawnBy: "",
  fasting: "",
  selectedProfiles: [],
  selectedTests: [],
  priority: "STAT",
  notes: "",
};

const LabReferralView = ({ onComplete }) => {
  const BASE_URL = process.env.REACT_APP_API_URL;
  const [form, setForm] = useState(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const toggleItem = (field, item) => {
    setForm((prev) => {
      const already = prev[field].some((i) => i.name === item.name);
      return {
        ...prev,
        [field]: already
          ? prev[field].filter((i) => i.name !== item.name)
          : [...prev[field], { name: item.name, code: item.code }],
      };
    });
  };

  const resetForm = () => {
    setForm(initialState);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedPatient(null);
  };

  const handleSearch = async (query) => {
    if (!query.trim()) { setSearchResults([]); return; }
    setIsSearching(true);
    try {
      const response = await fetch(`${BASE_URL}/ris/search?pat=${encodeURIComponent(query)}`);
      if (response.ok) setSearchResults(await response.json());
      else setSearchResults([]);
    } catch {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    setSearchResults([]);
    setSearchQuery('');
    setForm(prev => ({
      ...prev,
      firstName: patient.first_name || '',
      lastName: patient.last_name || '',
      dob: patient.dob || '',
      sex: patient.sex || '',
    }));
  };

  const handleClearPatient = () => {
    setSelectedPatient(null);
    setForm(prev => ({ ...prev, firstName: '', lastName: '', dob: '', sex: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.firstName.trim()) { toast.warning("Please enter the patient first name"); return; }
    if (!form.lastName.trim())  { toast.warning("Please enter the patient last name");  return; }
    if (!form.dob)              { toast.warning("Please enter the date of birth");       return; }
    if (!form.sex)              { toast.warning("Please select the patient sex");        return; }
    if (!form.orderingClinician.trim()) { toast.warning("Please enter the ordering clinician"); return; }
    if (!form.diagnosis.trim()) { toast.warning("Please enter the diagnosis"); return; }
    if (form.selectedProfiles.length === 0 && form.selectedTests.length === 0) {
      toast.warning("Please select at least one profile or lab test");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${BASE_URL}/labs/referral`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ris_patients_id: selectedPatient?.ris_patients_id || null,
          first_name: form.firstName.trim(),
          last_name: form.lastName.trim(),
          dob: form.dob,
          sex: form.sex,
          external_mrn: form.externalMrn.trim() || null,
          ordering_clinician: form.orderingClinician.trim(),
          diagnosis: form.diagnosis.trim(),
          specimen_date: form.specimenDate || null,
          specimen_time: form.specimenTime || null,
          drawn_by: form.drawnBy.trim() || null,
          fasting: form.fasting || null,
          selected_profiles: form.selectedProfiles,
          selected_tests: form.selectedTests,
          priority: form.priority,
          notes: form.notes.trim() || null,
        }),
      });

      const contentType = response.headers.get("content-type");
      const data = contentType?.includes("application/json")
        ? await response.json()
        : { msg: await response.text() };

      if (response.ok) {
        toast.success(data.msg || "Lab order submitted successfully!");
        resetForm();
        if (onComplete) onComplete();
      } else {
        toast.error(data.msg || "Failed to submit lab order");
      }
    } catch (error) {
      logger.error("Error submitting lab order:", error);
      toast.error("Error submitting lab order. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalSelected = form.selectedProfiles.length + form.selectedTests.length;

  return (
    <div className="modality-container">

      {/* ── Returning Patient Search ── */}
      <h2>Returning Patient Search</h2>

      {selectedPatient ? (
        <div className="im-selected-patient-banner" style={{ marginBottom: "1rem" }}>
          <span>
            <strong>{selectedPatient.first_name} {selectedPatient.last_name}</strong>
            &mdash; DOB: {selectedPatient.dob}
          </span>
          <button
            type="button"
            className="im-selected-patient-clear"
            onClick={handleClearPatient}
            title="Clear patient selection"
          >
            &times;
          </button>
        </div>
      ) : (
        <div className="im-search-wrapper" style={{ marginBottom: "1rem" }}>
          <div className="im-search-row">
            <input
              type="text"
              className="imaging-order-notes"
              style={{ flex: 1 }}
              placeholder="Search by name, DOB, or address…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleSearch(searchQuery))}
              disabled={isSubmitting}
            />
            <button
              type="button"
              className="oi-button"
              style={{ width: "auto", padding: "0.5rem 1.25rem", marginLeft: "8px" }}
              onClick={() => handleSearch(searchQuery)}
              disabled={isSubmitting || isSearching}
            >
              {isSearching ? 'Searching…' : 'Search'}
            </button>
          </div>
          {searchResults.length > 0 && (
            <div className="im-search-dropdown">
              {searchResults.map((p) => (
                <div
                  key={p.ris_patients_id}
                  className="im-search-result-item"
                  onClick={() => handleSelectPatient(p)}
                >
                  <strong>{p.first_name} {p.last_name}</strong>
                  &mdash; {p.dob} &mdash; {p.street_number} {p.street_name}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit}>

        {/* ── Patient Information ── */}
        <h2>Patient Information</h2>

        <div className="label-container">
          <div className="field">
            <label className="label-field">First Name <span style={{ color: "red" }}>*</span></label>
            <input
              type="text"
              className="imaging-order-notes"
              style={{ width: "100%", boxSizing: "border-box" }}
              placeholder="First name"
              value={form.firstName}
              onChange={set("firstName")}
              disabled={isSubmitting || !!selectedPatient}
            />
          </div>
          <div className="field">
            <label className="label-field">Last Name <span style={{ color: "red" }}>*</span></label>
            <input
              type="text"
              className="imaging-order-notes"
              style={{ width: "100%", boxSizing: "border-box" }}
              placeholder="Last name"
              value={form.lastName}
              onChange={set("lastName")}
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div className="label-container">
          <div className="field">
            <label className="label-field">Date of Birth <span style={{ color: "red" }}>*</span></label>
            <input
              type="date"
              className="imaging-order-notes"
              style={{ width: "100%", boxSizing: "border-box" }}
              value={form.dob}
              onChange={set("dob")}
              disabled={isSubmitting}
            />
          </div>
          <div className="field">
            <label className="label-field">Sex <span style={{ color: "red" }}>*</span></label>
            <select
              className="select-field"
              value={form.sex}
              onChange={set("sex")}
              disabled={isSubmitting}
            >
              <option value="">Select Sex</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </div>
        </div>

        <div className="label-container">
          <div className="field">
            <label className="label-field">External MRN <span style={{ color: "#9ca3af", fontWeight: 400 }}>(optional)</span></label>
            <input
              type="text"
              className="imaging-order-notes"
              style={{ width: "100%", boxSizing: "border-box" }}
              placeholder="External chart / MRN"
              value={form.externalMrn}
              onChange={set("externalMrn")}
              disabled={isSubmitting}
            />
          </div>
          <div className="field">
            {/* spacer */}
          </div>
        </div>

        {/* ── Order Information ── */}
        <h2>Order Information</h2>

        <div className="label-container">
          <div className="field">
            <label className="label-field">Ordering Clinician <span style={{ color: "red" }}>*</span></label>
            <input
              type="text"
              className="imaging-order-notes"
              style={{ width: "100%", boxSizing: "border-box" }}
              placeholder="Dr. First Last"
              value={form.orderingClinician}
              onChange={set("orderingClinician")}
              disabled={isSubmitting}
            />
          </div>
          <div className="field">
            <label className="label-field">Diagnosis <span style={{ color: "red" }}>*</span></label>
            <input
              type="text"
              className="imaging-order-notes"
              style={{ width: "100%", boxSizing: "border-box" }}
              placeholder="Diagnosis code / description"
              value={form.diagnosis}
              onChange={set("diagnosis")}
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div className="label-container">
          <div className="field">
            <label className="label-field">Priority</label>
            <select
              className="select-field"
              value={form.priority}
              onChange={set("priority")}
              disabled={isSubmitting}
            >
              <option value="STAT">STAT</option>
              <option value="Routine">Routine</option>
            </select>
          </div>
          <div className="field">
            <label className="label-field">Fasting Specimen</label>
            <select
              className="select-field"
              value={form.fasting}
              onChange={set("fasting")}
              disabled={isSubmitting}
            >
              <option value="">Select…</option>
              <option value="YES">Yes</option>
              <option value="NO">No</option>
            </select>
          </div>
        </div>

        {/* ── Specimen Information ── */}
        <h2>Specimen Information</h2>

        <div className="label-container">
          <div className="field">
            <label className="label-field">Specimen Date</label>
            <input
              type="date"
              className="imaging-order-notes"
              style={{ width: "100%", boxSizing: "border-box" }}
              value={form.specimenDate}
              onChange={set("specimenDate")}
              disabled={isSubmitting}
            />
          </div>
          <div className="field">
            <label className="label-field">Specimen Time</label>
            <input
              type="time"
              className="imaging-order-notes"
              style={{ width: "100%", boxSizing: "border-box" }}
              value={form.specimenTime}
              onChange={set("specimenTime")}
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div className="label-container">
          <div className="field">
            <label className="label-field">Drawn By</label>
            <input
              type="text"
              className="imaging-order-notes"
              style={{ width: "100%", boxSizing: "border-box" }}
              placeholder="Staff name"
              value={form.drawnBy}
              onChange={set("drawnBy")}
              disabled={isSubmitting}
            />
          </div>
          <div className="field">
            {/* spacer */}
          </div>
        </div>

        {/* ── Lab Selection ── */}
        <h2>Lab Selection</h2>

        <div className="label-container">
          <fieldset className="options">
            <legend style={{ fontWeight: 600, fontSize: "0.95rem" }}>
              Profiles
              {form.selectedProfiles.length > 0 && (
                <span style={{ color: "#2563eb", marginLeft: "8px" }}>
                  ({form.selectedProfiles.length} selected)
                </span>
              )}
            </legend>
            {LAB_PROFILES.map((profile) => (
              <label key={profile.code}>
                <input
                  type="checkbox"
                  checked={form.selectedProfiles.some((p) => p.name === profile.name)}
                  onChange={() => toggleItem("selectedProfiles", profile)}
                  disabled={isSubmitting}
                  style={{ marginRight: "8px" }}
                />
                {profile.name}
              </label>
            ))}
          </fieldset>

          <fieldset className="options">
            <legend style={{ fontWeight: 600, fontSize: "0.95rem" }}>
              Individual Tests
              {form.selectedTests.length > 0 && (
                <span style={{ color: "#2563eb", marginLeft: "8px" }}>
                  ({form.selectedTests.length} selected)
                </span>
              )}
            </legend>
            {labTests.filter(t => !t.disabled).map((test) => (
              <label key={test.code + test.name}>
                <input
                  type="checkbox"
                  checked={form.selectedTests.some((t) => t.name === test.name)}
                  onChange={() => toggleItem("selectedTests", test)}
                  disabled={isSubmitting}
                  style={{ marginRight: "8px" }}
                />
                {test.name}
                <span style={{ color: "#9ca3af", fontSize: "0.8rem", marginLeft: "6px" }}>
                  — {test.code}
                </span>
              </label>
            ))}
          </fieldset>
        </div>

        {/* ── Notes ── */}
        <div className="field">
          <label className="label-field">
            Notes <span style={{ color: "#9ca3af", fontWeight: 400 }}>(optional)</span>
          </label>
          <textarea
            className="imaging-order-notes"
            rows={3}
            style={{ width: "100%", boxSizing: "border-box" }}
            placeholder="Additional notes or clinical context..."
            value={form.notes}
            onChange={set("notes")}
            disabled={isSubmitting}
          />
        </div>

        {/* ── Actions ── */}
        <div style={{ display: "flex", gap: "12px", marginTop: "0.5rem" }}>
          <button
            type="button"
            className="oi-button"
            style={{ background: "#6b7280" }}
            onClick={resetForm}
            disabled={isSubmitting}
          >
            Reset Form
          </button>
          <button
            type="submit"
            className="oi-button"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Submitting…"
              : `Submit Lab Order${totalSelected > 0 ? ` (${totalSelected} item${totalSelected > 1 ? "s" : ""})` : ""}`
            }
          </button>
        </div>

      </form>
    </div>
  );
};

export default LabReferralView;
